/**
 * Phone validator for the widget's Contact step.
 *
 * Pure US-mobile / US-landline check. Returns the E.164 form on success,
 * or a {reason} we can render under the input on failure. Pulls in zero
 * deps to stay inside the 30 KB widget bundle budget — libphonenumber-js
 * (server-side) is ~50 KB itself and would dominate.
 *
 * Why this exists:
 * Square's CreateBooking validator silently rejects phone numbers in the
 * NANP-reserved 555-01XX range (the *officially-fictitious* range used in
 * docs and TV scripts) plus a handful of other shapes. The previous
 * widget validator only checked digit count, so a user could type
 * "555-010-1234" — pass client validation, pass our setClient handler,
 * advance to Confirm, then have CreateBooking 400 with no useful error
 * surfaced anywhere. This blocks early.
 */

export type PhoneValidationResult =
  | { ok: true; e164: string }
  | { ok: false; reason: string };

/**
 * NANP reserved fictitious-use range: area-code-555-01XX. Everything
 * else (including 555-1212 directory assistance) is fine. Digits are
 * already stripped of separators when this runs, so we test the
 * exchange (positions 3-5) and line prefix (positions 6-7) directly.
 */
function isReservedFictitious(tenDigits: string): boolean {
  return tenDigits.slice(3, 6) === '555' && tenDigits.slice(6, 8) === '01';
}

/**
 * "1111111111", "0000000000", etc. — never legitimate, common in test
 * data, blocks bots that pattern-fill forms.
 */
function isAllSameDigit(tenDigits: string): boolean {
  return /^(\d)\1{9}$/.test(tenDigits);
}

/**
 * Sequential like "1234567890" / "0123456789" — also fake-looking and
 * Square doesn't love these either.
 */
function isSequential(tenDigits: string): boolean {
  return tenDigits === '1234567890' || tenDigits === '0123456789';
}

/**
 * NANP area codes can't start with 0 or 1, and exchange codes (positions
 * 4-6) can't start with 0 or 1 either. Catches "0205551234" /
 * "1205551234" typos that would silently 400 at Square.
 */
function hasValidNanpStructure(tenDigits: string): boolean {
  const areaCode = tenDigits[0];
  const exchange = tenDigits[3];
  if (!areaCode || !exchange) return false;
  return areaCode !== '0' && areaCode !== '1' && exchange !== '0' && exchange !== '1';
}

export function validatePhone(input: string): PhoneValidationResult {
  const raw = input.trim();
  if (!raw) return { ok: false, reason: 'Phone number is required.' };

  // Strip everything except digits and the leading + (which marks E.164).
  let digits = raw.replace(/[^\d+]/g, '');

  // Strip a leading "+1" or "1" so we always work with the 10 NANP digits.
  if (digits.startsWith('+1')) digits = digits.slice(2);
  else if (digits.startsWith('+')) {
    return {
      ok: false,
      reason: 'Only US numbers are supported. Drop the country code.',
    };
  } else if (digits.length === 11 && digits.startsWith('1')) {
    digits = digits.slice(1);
  }

  if (digits.length !== 10) {
    return {
      ok: false,
      reason: 'Please enter a 10-digit US phone number.',
    };
  }

  if (!hasValidNanpStructure(digits)) {
    return {
      ok: false,
      reason: 'That doesn’t look like a valid US number. Check the area code.',
    };
  }

  if (isAllSameDigit(digits) || isSequential(digits)) {
    return {
      ok: false,
      reason: 'That number isn’t valid. Please use your mobile.',
    };
  }

  if (isReservedFictitious(digits)) {
    return {
      ok: false,
      reason:
        '555-01XX numbers are reserved for fictional use. Please use your real mobile.',
    };
  }

  return { ok: true, e164: '+1' + digits };
}
